import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import moment from "moment";
import unsplash from "../api/unsplash";
import useConsulenzaData, { consulenzaAuth } from "./useConsulenzaData";
import { decimalFieldSx } from "./consulenzaFields";
const euro = (v) =>
  Number(v).toLocaleString("it-IT", { style: "currency", currency: "EUR" });
const modi = {
  nessuno: "Nessun addebito",
  forfait: "Forfait",
  chilometrico: "Chilometrico",
};
export default function RimborsiConsulenza({ clienteId, mese, onApriUscite }) {
  const config = useConsulenzaData("rimborsiConsulenza", clienteId);
  const preview = useConsulenzaData(
    "rimborsiConsulenza/riepilogo",
    clienteId,
    mese,
  );
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [evitaPedaggi, setEvitaPedaggi] = useState(false);
  const [calcoloSede, setCalcoloSede] = useState(null);
  const [proposte, setProposte] = useState({});
  const versioneCalcolo = useRef(0);
  useEffect(
    () => () => {
      versioneCalcolo.current += 1;
    },
    [clienteId],
  );
  const calcolaKm = async (sede) => {
    const versione = ++versioneCalcolo.current;
    setCalcoloSede(sede);
    setError("");
    setMessage("");
    setProposte((p) => {
      const next = { ...p };
      delete next[sede];
      return next;
    });
    try {
      const response = await unsplash.post(
        "rimborsiConsulenza/percorso",
        {
          cliente: clienteId,
          sede,
          evitaPedaggi,
        },
        consulenzaAuth(),
      );
      if (versione === versioneCalcolo.current)
        setProposte((p) => ({ ...p, [sede]: response.data.data }));
    } catch (e) {
      if (versione === versioneCalcolo.current)
        setError(
          e.response?.data?.message ||
            "Impossibile calcolare i km A/R. Puoi inserirli manualmente.",
        );
    } finally {
      if (versione === versioneCalcolo.current) setCalcoloSede(null);
    }
  };
  const usaProposta = (sede) => {
    const proposta = proposte[sede];
    if (!proposta) return;
    change(
      "sedi",
      form.sedi.map((s) =>
        s.ID === sede ? { ...s, Km: proposta.KmTotali } : s,
      ),
    );
    setMessage(
      "Km A/R proposti riportati nel campo. Puoi modificarli; premi Salva condizioni per confermarli.",
    );
  };
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  useEffect(() => {
    setForm(
      config.data
        ? {
            Modalita: config.data.configurazione?.Modalita || "nessuno",
            Forfait: config.data.configurazione?.Forfait ?? "",
            TariffaKm: config.data.configurazione?.TariffaKm ?? "",
            sedi: config.data.sedi.map((s) => ({ ...s, Km: s.Km ?? "" })),
          }
        : null,
    );
  }, [config.data]);
  const change = (key, value) => {
    setMessage("");
    setForm((p) => ({ ...p, [key]: value }));
  };
  const save = async () => {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      await unsplash.put(
        "rimborsiConsulenza",
        { cliente: clienteId, configurazione: form },
        consulenzaAuth(),
      );
      await config.reload();
      setMessage(
        "Condizioni salvate. Gli importi delle uscite già calcolate restano invariati.",
      );
    } catch (e) {
      setError(
        e.response?.data?.message || "Impossibile salvare le condizioni",
      );
    } finally {
      setBusy(false);
    }
  };
  const complete = async () => {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const r = await unsplash.post(
        "rimborsiConsulenza/completa",
        { cliente: clienteId, mese },
        consulenzaAuth(),
      );
      await preview.reload();
      setMessage(
        `${r.data.data.aggiornate} uscite completate con le condizioni salvate. Le uscite con dati ancora mancanti restano da completare.`,
      );
    } catch (e) {
      setError(e.response?.data?.message || "Impossibile completare le uscite");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Stack
      spacing={2}
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        p: 2,
      }}
    >
      <Box
        sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}
      >
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          Rimborsi spese al cliente
        </Typography>
        {onApriUscite && (
          <Button variant="outlined" size="small" onClick={onApriUscite}>
            Apri Uscite tecnici
          </Button>
        )}
      </Box>
      <Typography variant="body2" color="text.secondary">
        1. Salva le condizioni di rimborso concordate con il cliente. 2. Il
        tecnico registra sede e data della visita in “Uscite tecnici”. 3. Koala
        applica le condizioni salvate e riepiloga gli addebiti nel mese della
        visita, separatamente dai canoni.
      </Typography>
      {(config.error || preview.error || error) && (
        <Alert severity="error">{error || config.error || preview.error}</Alert>
      )}
      {message && <Alert severity="success">{message}</Alert>}
      {config.loading && (
        <CircularProgress size={20} aria-label="Caricamento rimborsi" />
      )}
      {form && (
        <>
          {!config.data?.configurazione && (
            <Alert severity="info">
              Condizioni non ancora configurate. Le uscite restano da completare
              finché non salvi una modalità, anche “nessun rimborso”.
            </Alert>
          )}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={form.Modalita !== "nessuno"}
                  disabled={busy || calcoloSede !== null}
                  onChange={(e) =>
                    change("Modalita", e.target.checked ? "forfait" : "nessuno")
                  }
                />
              }
              label="Rimborso previsto"
            />
            {form.Modalita !== "nessuno" && (
              <TextField
                size="small"
                select
                label="Modalità rimborso"
                value={form.Modalita}
                SelectProps={{ native: true }}
                disabled={busy || calcoloSede !== null}
                onChange={(e) => change("Modalita", e.target.value)}
              >
                <option value="forfait">Forfait per uscita</option>
                <option value="chilometrico">Chilometrico</option>
              </TextField>
            )}
            {form.Modalita === "forfait" && (
              <TextField
                size="small"
                type="number"
                label="Forfait per uscita (€)"
                value={form.Forfait}
                disabled={busy || calcoloSede !== null}
                sx={decimalFieldSx}
                inputProps={{ min: 0, step: "0.01" }}
                onChange={(e) => change("Forfait", e.target.value)}
              />
            )}
            {form.Modalita === "chilometrico" && (
              <TextField
                size="small"
                type="number"
                label="Tariffa chilometrica (€/km)"
                value={form.TariffaKm}
                disabled={busy || calcoloSede !== null}
                sx={decimalFieldSx}
                inputProps={{ min: 0, step: "0.01" }}
                onChange={(e) => change("TariffaKm", e.target.value)}
              />
            )}
            <Button
              variant="contained"
              size="small"
              disabled={busy || calcoloSede !== null}
              onClick={save}
            >
              Salva condizioni
            </Button>
          </Box>
          {form.Modalita === "chilometrico" && (
            <>
              <Typography variant="body2" color="text.secondary">
                Partenza del calcolo: Via dell’Industria 1, 20074 Carpiano (MI).
                Il calcolo stradale viene richiesto solo qui, durante la
                configurazione, e somma sempre andata e ritorno. La
                registrazione delle uscite usa i km salvati senza richiamare il
                servizio.
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={evitaPedaggi}
                    disabled={busy || calcoloSede !== null}
                    onChange={(e) => {
                      setEvitaPedaggi(e.target.checked);
                      setProposte({});
                    }}
                  />
                }
                label="Evita pedaggi nel calcolo A/R"
              />
              <Typography variant="body2" color="text.secondary">
                Inserisci i chilometri complessivi da addebitare per un’uscita
                in ciascuna sede, oppure usa una proposta stradale A/R. Per le
                uscite Koala usa il valore salvato, senza ricalcolarlo né
                raddoppiarlo. I km devono comprendere già andata e ritorno.
              </Typography>
              {!form.sedi.length && (
                <Alert severity="warning">
                  Aggiungi una sede al cliente prima di configurare i
                  chilometri.
                </Alert>
              )}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
                  gap: 1.5,
                }}
              >
                {form.sedi.map((s, i) => {
                  const posizione =
                    [s.Lat, s.Lon].every(
                      (v) =>
                        v !== null &&
                        v !== undefined &&
                        String(v).trim() !== "" &&
                        Number.isFinite(Number(v)),
                    ) &&
                    Math.abs(Number(s.Lat)) <= 90 &&
                    Math.abs(Number(s.Lon)) <= 180 &&
                    !(Number(s.Lat) === 0 && Number(s.Lon) === 0);
                  const proposta = proposte[s.ID];
                  return (
                    <Stack
                      key={s.ID}
                      spacing={1}
                      sx={{
                        p: 1.5,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                      }}
                    >
                      <TextField
                        size="small"
                        type="number"
                        label={`${s.Descrizione || `Sede ${s.ID}`} — km per uscita`}
                        value={s.Km}
                        disabled={busy || calcoloSede !== null}
                        sx={decimalFieldSx}
                        inputProps={{ min: 0, step: "0.01" }}
                        onChange={(e) =>
                          change(
                            "sedi",
                            form.sedi.map((site, j) =>
                              j === i ? { ...site, Km: e.target.value } : site,
                            ),
                          )
                        }
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={!posizione || busy || calcoloSede !== null}
                        onClick={() => calcolaKm(s.ID)}
                        aria-label={`Calcola km A/R per ${s.Descrizione || `Sede ${s.ID}`}`}
                      >
                        {calcoloSede === s.ID
                          ? "Calcolo A/R…"
                          : "Calcola km A/R"}
                      </Button>
                      {!posizione && (
                        <Typography variant="caption" color="text.secondary">
                          Posizione assente o non valida: aggiungi le coordinate
                          nella scheda sede o inserisci i km manualmente.
                        </Typography>
                      )}
                      {proposta && (
                        <Alert severity="info">
                          Proposta Mapbox: {proposta.KmAndata} km andata +{" "}
                          {proposta.KmRitorno} km ritorno = {proposta.KmTotali}{" "}
                          km A/R.{" "}
                          {proposta.EvitaPedaggi
                            ? "Pedaggi esclusi."
                            : "Pedaggi consentiti."}
                          {proposta.PartenzaInterpolata &&
                            " La posizione del civico di partenza è stimata: verifica il percorso."}
                          <Button
                            size="small"
                            disabled={busy || calcoloSede !== null}
                            onClick={() => usaProposta(s.ID)}
                            aria-label={`Usa proposta A/R per ${s.Descrizione || `Sede ${s.ID}`}`}
                          >
                            Usa {proposta.KmTotali} km A/R
                          </Button>
                        </Alert>
                      )}
                    </Stack>
                  );
                })}
              </Box>
            </>
          )}
        </>
      )}
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}
      >
        <Typography variant="subtitle1">
          Riepilogo uscite effettuate —{" "}
          {moment(mese, "YYYY-MM").format("MM/YYYY")}
        </Typography>
        <Button
          size="small"
          disabled={preview.loading || busy}
          onClick={preview.reload}
        >
          Aggiorna elenco
        </Button>
        <Typography sx={{ ml: { sm: "auto" }, fontWeight: 600 }}>
          Totale rimborsi: {euro(preview.data?.totale || 0)}
        </Typography>
      </Box>
      {preview.data?.daCompletare > 0 && (
        <Alert
          severity="warning"
          action={
            <Button
              color="inherit"
              size="small"
              disabled={busy || calcoloSede !== null}
              onClick={complete}
            >
              Calcola da completare
            </Button>
          }
        >
          {preview.data.daCompletare} uscite da completare, escluse dal totale.
          Il calcolo usa le condizioni salvate e non modifica gli importi già
          calcolati.
        </Alert>
      )}
      {preview.loading && (
        <CircularProgress size={18} aria-label="Caricamento uscite mensili" />
      )}
      {preview.data &&
        (!preview.data.righe.length ? (
          <Typography color="text.secondary">
            Nessuna uscita registrata nel mese selezionato. Il riepilogo si
            popola quando i tecnici registrano le visite in “Uscite tecnici”.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {[
                    "Data",
                    "Tecnico",
                    "Sede",
                    "Modalità",
                    "Km",
                    "€/km",
                    "Importo",
                  ].map((l) => (
                    <TableCell key={l}>{l}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {preview.data.righe.map((r) => (
                  <TableRow key={r.ID}>
                    <TableCell>{moment(r.Data).format("DD/MM/YYYY")}</TableCell>
                    <TableCell>{r.Tecnico}</TableCell>
                    <TableCell>{r.SedeDescrizione}</TableCell>
                    <TableCell>
                      {modi[r.Modalita] || "Da configurare"}
                    </TableCell>
                    <TableCell>{r.Km ?? "—"}</TableCell>
                    <TableCell>
                      {r.TariffaKm === null ? "—" : euro(r.TariffaKm)}
                    </TableCell>
                    <TableCell>
                      {r.Importo === null ? "Da completare" : euro(r.Importo)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ))}
      <Typography variant="caption" color="text.secondary">
        Rimborsi da addebitare al cliente, separati dai canoni. Il riepilogo non
        registra fatture. Le uscite si inseriscono nella scheda “Uscite
        tecnici”.
      </Typography>
    </Stack>
  );
}
